import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Modal from '@mui/material/Modal';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_HOVER,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';

import type { AlertConfig, AlertThresholds, Integration } from '../../../../services/api/integrations.api';
import { ALERT_DEFAULTS, useUpdateIntegrationAlertConfigMutation } from '../../../../services/api/integrations.api';

interface AlertConfigModalProps {
  integration: Integration;
  open: boolean;
  onClose: () => void;
}

interface RowState {
  enabled: boolean;
  value: number;
}

function toRowState(value: number | null, defaultValue: number): RowState {
  return { enabled: value !== null, value: value ?? defaultValue };
}

function toThresholdValue(row: RowState): number | null {
  return row.enabled ? row.value : null;
}

const AlertConfigModal: React.FC<AlertConfigModalProps> = ({ integration, open, onClose }) => {
  const existing = integration.alertConfig?.global;

  const [consecutive, setConsecutive] = useState<RowState>(() =>
    toRowState(existing?.consecutiveErrors ?? ALERT_DEFAULTS.consecutiveErrors, ALERT_DEFAULTS.consecutiveErrors!)
  );
  const [hourly, setHourly] = useState<RowState>(() =>
    toRowState(existing?.hourlyThreshold ?? ALERT_DEFAULTS.hourlyThreshold, ALERT_DEFAULTS.hourlyThreshold!)
  );
  const [daily, setDaily] = useState<RowState>(() =>
    toRowState(existing?.dailyThreshold ?? ALERT_DEFAULTS.dailyThreshold, ALERT_DEFAULTS.dailyThreshold!)
  );

  const [update, { isLoading }] = useUpdateIntegrationAlertConfigMutation();

  const handleSave = async () => {
    const global: AlertThresholds = {
      consecutiveErrors: toThresholdValue(consecutive),
      hourlyThreshold: toThresholdValue(hourly),
      dailyThreshold: toThresholdValue(daily),
    };
    const alertConfig: AlertConfig = {
      global,
      byBusinessUnit: integration.alertConfig?.byBusinessUnit ?? {},
    };
    await update({ id: integration.id, alertConfig });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          bgcolor: GLASS_BACKGROUND,
          backdropFilter: GLASS_BLUR,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: 4,
          outline: 'none',
        }}
      >
        <Typography variant="h6" sx={{ color: NAVY, fontFamily: 'Jost', fontWeight: 700, mb: 3 }}>
          Configuración de alertas
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: NAVY,
            opacity: 0.5,
            fontFamily: 'Jost',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          Umbrales globales
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <AlertRow
            label="Errores consecutivos"
            description="Alerta tras N errores seguidos"
            state={consecutive}
            onChange={setConsecutive}
            min={1}
            adornment="errores"
          />
          <AlertRow
            label="Tasa de éxito por hora"
            description="Alerta cuando la tasa de la última hora baje del umbral"
            state={hourly}
            onChange={setHourly}
            min={1}
            max={99}
            adornment="%"
          />
          <AlertRow
            label="Tasa de éxito diaria"
            description="Alerta cuando la tasa de las últimas 24h baje del umbral"
            state={daily}
            onChange={setDaily}
            min={1}
            max={99}
            adornment="%"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            sx={{
              color: NAVY,
              fontFamily: 'Jost',
              borderRadius: RADIUS_COIN,
              px: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: SURFACE_HOVER },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            sx={{
              bgcolor: YELLOW,
              color: NAVY,
              fontFamily: 'Jost',
              fontWeight: 700,
              borderRadius: RADIUS_COIN,
              px: 3,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: alpha(YELLOW, 0.85), boxShadow: 'none' },
              '&:disabled': { bgcolor: YELLOW, opacity: 0.5 },
            }}
            variant="contained"
          >
            {isLoading ? 'Guardando…' : 'Guardar'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

interface AlertRowProps {
  label: string;
  description: string;
  state: RowState;
  onChange: (s: RowState) => void;
  min?: number;
  max?: number;
  adornment: string;
}

const AlertRow: React.FC<AlertRowProps> = ({ label, description, state, onChange, min = 1, max, adornment }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      bgcolor: SURFACE_TINT,
      borderRadius: RADIUS_ITEM,
      px: 2,
      py: 1.5,
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ color: NAVY, fontFamily: 'Jost', fontWeight: 600, fontSize: '0.875rem' }}>{label}</Typography>
      <Typography sx={{ color: NAVY, opacity: 0.5, fontFamily: 'Jost', fontSize: '0.75rem' }}>{description}</Typography>
    </Box>

    <TextField
      type="number"
      value={state.value}
      disabled={!state.enabled}
      onChange={(e) => onChange({ ...state, value: Math.max(min, Math.min(max ?? 9999, Number(e.target.value))) })}
      slotProps={{
        htmlInput: {
          min,
          max,
          style: { textAlign: 'center', width: 48, padding: '6px 0', fontFamily: 'Jost', color: NAVY },
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontFamily: 'Jost', fontSize: '0.75rem', color: NAVY, opacity: 0.5 }}>
                {adornment}
              </Typography>
            </InputAdornment>
          ),
          disableUnderline: true,
          sx: {
            bgcolor: state.enabled ? SURFACE_HOVER : 'transparent',
            borderRadius: RADIUS_ITEM,
            px: 1,
            transition: 'background 0.2s',
          },
        },
      }}
      variant="standard"
      sx={{ width: 100 }}
    />

    <Switch
      checked={state.enabled}
      onChange={(e) => onChange({ ...state, enabled: e.target.checked })}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: YELLOW },
      }}
    />
  </Box>
);

export default AlertConfigModal;
